import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Building2, TrendingUp, Users } from "lucide-react";
import { Link } from "react-router-dom";
import heroImage from "@/assets/hero-image.jpg";

export function HeroSection() {
  return (
    <section className="relative bg-gradient-hero text-primary-foreground overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: `url(${heroImage})` }}
      />
      <div className="absolute inset-0 bg-gradient-hero/90" />
      
      {/* Content */}
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <Badge variant="secondary" className="mb-6 bg-secondary/20 text-secondary border-secondary/30 text-lg px-6 py-2">
            🚀 Australia's Premier Business Marketplace
          </Badge>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Connect Buyers & Sellers.
            <span className="block text-secondary">Transform Business Sales.</span>
          </h1>

          {/* Subheading */}
          <p className="text-xl lg:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
            The only platform business sellers need. <span className="text-secondary font-semibold">Available on web and mobile app.</span> List businesses starting from $89/month for brokers, 
            pay $15-$19 per qualified lead. No commissions, no lock-ins. <span className="text-secondary font-semibold">Download our app for instant notifications.</span>
          </p>

          {/* Testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-5xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-left border border-white/20">
              <div className="text-secondary mb-3">⭐️⭐️⭐️⭐️⭐️</div>
              <p className="text-lg font-semibold text-primary-foreground mb-3">"A genuinely different way to sell a business"</p>
              <p className="text-sm text-primary-foreground/80 mb-4">We listed our business on Biz Match after being frustrated with the usual platforms. The difference was immediate, real buyer interest, proper matching, and no commission pressure. It feels like the platform is actually working for sellers, not just listing and forgetting.</p>
              <p className="text-sm text-secondary font-medium">Michael R., Business Owner (NSW)</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-left border border-white/20">
              <div className="text-secondary mb-3">⭐️⭐️⭐️⭐️⭐️</div>
              <p className="text-lg font-semibold text-primary-foreground mb-3">"More qualified enquiries, less time wasted"</p>
              <p className="text-sm text-primary-foreground/80 mb-4">As a broker, one of the biggest challenges is filtering time-wasters. Biz Match has delivered far more relevant enquiries than we're used to, and the pay-per-lead model makes sense. It's refreshing to see a platform built around outcomes, not just exposure.</p>
              <p className="text-sm text-secondary font-medium">Sarah L., Business Broker</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-left border border-white/20">
              <div className="text-secondary mb-3">⭐️⭐️⭐️⭐️⭐️</div>
              <p className="text-lg font-semibold text-primary-foreground mb-3">"Simple, transparent, and cost-effective"</p>
              <p className="text-sm text-primary-foreground/80 mb-4">What stood out for me was the simplicity. No long contracts, no commissions, and clear pricing. Within weeks we had multiple interested parties that actually matched our criteria. Biz Match does exactly what it says it will.</p>
              <p className="text-sm text-secondary font-medium">David K., SME Seller</p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 relative z-10">
            <Button asChild size="xl" className="group w-full sm:w-auto min-h-[56px] touch-manipulation">
              <Link to="/list-business" className="flex items-center justify-center">
                Start Your Listing
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button asChild variant="hero-outline" size="xl" className="w-full sm:w-auto min-h-[56px] touch-manipulation">
              <Link to="/explore" className="flex items-center justify-center">
                Browse Businesses
              </Link>
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-2xl mx-auto">
            <div className="flex flex-col items-center">
              <div className="p-3 bg-secondary/20 rounded-full mb-3">
                <Building2 className="h-6 w-6 text-secondary" />
              </div>
              <div className="text-2xl font-bold">500+</div>
              <div className="text-sm text-primary-foreground/70">Active Listings</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="p-3 bg-secondary/20 rounded-full mb-3">
                <Users className="h-6 w-6 text-secondary" />
              </div>
              <div className="text-2xl font-bold">4,000+</div>
              <div className="text-sm text-primary-foreground/70">Qualified Buyers</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="p-3 bg-secondary/20 rounded-full mb-3">
                <TrendingUp className="h-6 w-6 text-secondary" />
              </div>
              <div className="text-2xl font-bold">85%</div>
              <div className="text-sm text-primary-foreground/70">Success Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-10 w-72 h-72 bg-secondary/10 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-primary-light/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }} />
    </section>
  );
}